import { Button } from '@/components/ui/button';
import { DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { execute } from '@/graphql/execute';
import { LayerType, LayersQuery } from '@/graphql/graphql';
import { analytics } from '@/lib/analytics';
import { createLayerMutation, layerPropertiesQuery, layersQuery, updateLayerMutation } from '@/queries/layer.query';
import { Plus, Trash } from '@mynaui/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const LayerEditorContent: FunctionComponent<{
  projectId: string;
  layer?: Partial<LayersQuery['layers'][0]>;
  onSuccess: () => void;
}> = ({ projectId, layer, onSuccess }) => {
  const { t } = useTranslation('project');
  const { data: layersData } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: async () => {
      const result = await execute(layersQuery, { projectId });
      return result;
    },
  });
  const { data: layerPropertiesData } = useQuery({
    queryKey: ['layerProperties', layer?.id],
    queryFn: async () => {
      const result = await execute(layerPropertiesQuery, { layerId: layer?.id || '' });
      return result;
    },
  });
  const formik = useFormik<{
    name: string;
    description: string;
    parentLayerId: string | null;
    type: LayerType;
    propertyAliases: Array<{ property: string; alias: string }>;
  }>({
    initialValues: {
      name: layer?.name || '',
      description: layer?.description || '',
      parentLayerId: layer?.parentLayerId || null,
      type: layer?.type || LayerType.Group,
      propertyAliases: layer?.propertyAliases || [],
    },
    onSubmit: async (values) => {
      if (layer) {
        await _handleUpdateLayer(values);
      } else {
        await _handleCreateLayer(values);
      }
    },
  });

  const _handleUpdateLayer = async (values: {
    name: string;
    description: string;
    parentLayerId: string | null;
    propertyAliases: Array<{ property: string; alias: string }>;
  }) => {
    if (!layer) return;
    const toastId = toast.loading(t('layers.updatingLayer'));
    try {
      await execute(updateLayerMutation, {
        input: {
          id: layer.id!,
          name: values.name,
          description: values.description,
          parentLayerId: values.parentLayerId,
          propertyAliases: values.propertyAliases,
        },
      });
      analytics.capture('Layer Updated');
      toast.success(t('layers.layerUpdated'), { id: toastId });
      onSuccess();
    } catch (error) {
      toast.error(t('layers.failedUpdateLayer'), { id: toastId });
    }
  };

  const _handleCreateLayer = async (values: {
    name: string;
    description: string;
    parentLayerId: string | null;
    type: LayerType;
    propertyAliases: Array<{ property: string; alias: string }>;
  }) => {
    const toastId = toast.loading(t('layers.creatingLayer'));
    try {
      await execute(createLayerMutation, {
        input: {
          name: values.name,
          description: values.description,
          parentLayerId: values.parentLayerId,
          type: values.type,
          projectId,
        },
      });
      analytics.capture('Layer Created', { type: values.type });
      toast.success(t('layers.layerCreated'), { id: toastId });
      onSuccess();
    } catch (error) {
      toast.error(t('layers.failedCreateLayer'), { id: toastId });
    }
  };

  const _handleAddPropertyAlias = () => {
    formik.setFieldValue('propertyAliases', [...formik.values.propertyAliases, { property: '', alias: '' }]);
  };

  const _handleRemovePropertyAlias = (index: number) => {
    const newAliases = formik.values.propertyAliases.filter((_, i) => i !== index);
    formik.setFieldValue('propertyAliases', newAliases);
  };

  const _handlePropertyAliasChange = (index: number, field: 'property' | 'alias', value: string) => {
    const newAliases = [...formik.values.propertyAliases];
    newAliases[index] = { ...newAliases[index], [field]: value };
    formik.setFieldValue('propertyAliases', newAliases);
  };

  const parentLayerOptions = useMemo(() => {
    const layers = layersData?.layers ?? [];
    if (!layer?.id) return layers;

    const descendantIds = new Set<string>();
    const collectDescendants = (id: string) => {
      layers
        .filter((candidate) => candidate.parentLayerId === id)
        .forEach((candidate) => {
          descendantIds.add(candidate.id);
          collectDescendants(candidate.id);
        });
    };
    collectDescendants(layer.id);

    return layers.filter((candidate) => candidate.id !== layer.id && !descendantIds.has(candidate.id));
  }, [layer?.id, layersData?.layers]);

  return (
    <DialogContent className='sm:max-w-[520px]'>
      <DialogHeader>
        <DialogTitle>{layer ? t('layerEditor.editTitle') : t('layerEditor.createTitle')}</DialogTitle>
        <DialogDescription>
          {layer ? t('layerEditor.editDescription') : t('layerEditor.createDescription')}
        </DialogDescription>
      </DialogHeader>
      <div className='grid gap-4'>
        <div className='grid gap-3'>
          <Label htmlFor='name-1'>{t('layerEditor.name')}</Label>
          <Input
            id='name-1'
            name='name'
            value={formik.values.name}
            onChange={formik.handleChange}
            placeholder={t('layerEditor.namePlaceholder')}
          />
        </div>
        {!layer && (
          <div className='grid gap-3'>
            <Label htmlFor='type'>{t('layerEditor.type')}</Label>
            <Select
              defaultValue={formik.values.type}
              onValueChange={(value) => formik.setFieldValue('type', value as LayerType)}
              disabled={!!layer}
            >
              <SelectTrigger className='w-full'>
                <SelectValue placeholder={t('layerEditor.selectType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t('layerEditor.type')}</SelectLabel>
                  <SelectItem value='GROUP'>{t('layerEditor.folder')}</SelectItem>
                  <SelectItem value='VECTOR'>{t('layerEditor.vector')}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className='grid gap-3'>
          <Label htmlFor='description-1'>{t('layerEditor.description')}</Label>
          <Textarea
            id='description-1'
            name='description'
            value={formik.values.description}
            onChange={formik.handleChange}
            placeholder={t('layerEditor.descriptionPlaceholder')}
          />
        </div>
        <div className='grid gap-3'>
          <Label htmlFor='parentLayerId'>{t('layerEditor.parentLayer')}</Label>
          <Select
            value={formik.values.parentLayerId ?? '__root__'}
            onValueChange={(value) => formik.setFieldValue('parentLayerId', value === '__root__' ? null : value)}
          >
            <SelectTrigger id='parentLayerId' className='w-full'>
              <SelectValue placeholder={t('layerEditor.selectParentLayer')} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t('layerEditor.parentLayer')}</SelectLabel>
                <SelectItem value='__root__'>{t('layerEditor.noParentLayer')}</SelectItem>
                {parentLayerOptions.map((candidate) => (
                  <SelectItem key={candidate.id} value={candidate.id}>
                    {candidate.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        {['VECTOR'].includes(formik.values.type) && (
          <div className='grid gap-3'>
            <Label>{t('layerEditor.propertyAliases')}</Label>
            <div className='flex flex-col gap-2'>
              {formik.values.propertyAliases.map((alias, index) => (
                <div key={index} className='flex items-center gap-2'>
                  <Select
                    value={alias.property}
                    onValueChange={(value) => _handlePropertyAliasChange(index, 'property', value)}
                  >
                    <SelectTrigger className='flex-1'>
                      <SelectValue placeholder={t('layerEditor.selectProperty')} />
                    </SelectTrigger>
                    <SelectContent>
                      {layerPropertiesData?.layerProperties?.map((prop: string) => (
                        <SelectItem key={prop} value={prop}>
                          {prop}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder={t('layerEditor.alias')}
                    value={alias.alias}
                    onChange={(e) => _handlePropertyAliasChange(index, 'alias', e.target.value)}
                    className='flex-1'
                  />
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => _handleRemovePropertyAlias(index)}
                    className='size-9 shrink-0'
                  >
                    <Trash className='size-4' />
                  </Button>
                </div>
              ))}
              <Button type='button' variant='outline' onClick={_handleAddPropertyAlias} className='w-full'>
                <Plus className='size-4 mr-2' />
                {t('layerEditor.addPropertyAlias')}
              </Button>
            </div>
          </div>
        )}
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant='outline'>{t('layerEditor.cancel')}</Button>
        </DialogClose>
        <DialogClose asChild>
          <Button type='submit' onClick={formik.submitForm} disabled={formik.isSubmitting}>
            {t('layerEditor.saveChanges')}
          </Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
};
